import { useEffect, useState, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { scrollToFirstError } from '@/lib/form'
import { formatEur } from '@/lib/format'

interface TrainingRequest {
  id: string
  topic: string
  description: string
  targetCount: number
  preferredDate: string | null
  budget: number | null
  location: string | null
  isRemote: boolean
  requiresQualiopi: boolean
  status: string
  createdAt: string
  company: { id: string; name: string; logoUrl: string | null }
  _count: { proposals: number }
  proposals: { id: string; status: string }[]
}

interface MyProposal {
  id: string
  price: number
  message: string | null
  status: string
  createdAt: string
  request: { id: string; topic: string; status: string; company: { name: string } }
}

interface SpecialtyConfig {
  specialty: string
  label: string
  shortLabel: string
  icon: string
  description: string
  category: string
  active: boolean
}

const STATUS_LABEL: Record<string, string> = { OPEN: 'Ouverte', ASSIGNED: 'Attribuée', COMPLETED: 'Terminée', CANCELLED: 'Annulée' }
const PROPOSAL_STATUS_LABEL: Record<string, string> = { PENDING: 'En attente', ACCEPTED: 'Acceptée', DECLINED: 'Refusée' }
const PROPOSAL_STATUS_COLOR: Record<string, string> = {
  PENDING: 'bg-accent/10 border-accent/25 text-accent',
  ACCEPTED: 'bg-success/10 border-success/25 text-success',
  DECLINED: 'bg-white/[0.06] border-white/[0.10] text-text-muted',
}
const DURATION_OPTIONS = [
  { label: '1 heure', value: 60 }, { label: '2 heures', value: 120 }, { label: '3 heures', value: 180 },
  { label: '4 heures (demi-journée)', value: 240 }, { label: '7 heures (journée)', value: 420 },
  { label: '14 heures (2 jours)', value: 840 }, { label: '21 heures (3 jours)', value: 1260 },
]

function parseLocation(loc: string | null) {
  if (!loc) return { address: '', city: '', postalCode: '' }
  const parts = loc.split(',')
  const address = parts[0]?.trim() || ''
  const rest = parts[1]?.trim() || ''
  const postalMatch = rest.match(/\b(\d{5})\b/)
  const postalCode = postalMatch?.[1] || ''
  const city = rest.replace(postalCode, '').replace(/\s+/g, ' ').trim()
  return { address, city, postalCode }
}

type Tab = 'available' | 'my-proposals'

export default function CFFormationsPage() {
  const [tab, setTab] = useState<Tab>('available')
  const [available, setAvailable] = useState<TrainingRequest[]>([])
  const [myProposals, setMyProposals] = useState<MyProposal[]>([])
  const [specialtyConfigs, setSpecialtyConfigs] = useState<SpecialtyConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<TrainingRequest | null>(null)

  // Proposition state
  const [proposing, setProposing] = useState(false)
  const [proposalError, setProposalError] = useState('')
  const [proposalSuccess, setProposalSuccess] = useState('')

  // Recherche prestataire state
  const [searchMode, setSearchMode] = useState(false)
  const [searchSpecialties, setSearchSpecialties] = useState<Record<string, number>>({})
  const [searchSubmitting, setSearchSubmitting] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [searchResults, setSearchResults] = useState<Record<string, { id: string }>>({})
  const [searchForm, setSearchForm] = useState({
    title: '', description: '', date: '', duration: 420,
    address: '', city: '', postalCode: '', amount: 0, radiusKm: 30, minParticipants: 1,
  })

  useEffect(() => {
    try {
      const stored = localStorage.getItem('cf_search_results')
      if (stored) setSearchResults(JSON.parse(stored))
    } catch {}
  }, [])

  useEffect(() => {
    Promise.all([
      apiFetch<TrainingRequest[]>('/training-requests/cf/available'),
      apiFetch<MyProposal[]>('/training-requests/cf/my-proposals'),
      apiFetch<SpecialtyConfig[]>('/public/specialty-config'),
    ])
      .then(([av, mp, sc]) => { setAvailable(av); setMyProposals(mp); setSpecialtyConfigs(sc) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  function openDetail(r: TrainingRequest) {
    if (selected?.id === r.id) { setSelected(null); return }
    const { address, city, postalCode } = parseLocation(r.location)
    setSelected(r)
    setProposalError('')
    setProposalSuccess('')
    setSearchMode(false)
    setSearchSpecialties({})
    setSearchForm({
      title: r.topic,
      description: r.description,
      date: r.preferredDate ? new Date(r.preferredDate).toISOString().slice(0, 16) : '',
      duration: 420,
      address,
      city,
      postalCode,
      amount: r.budget != null ? Math.round(r.budget / 100) : 0,
      radiusKm: 30,
      minParticipants: r.targetCount || 1,
    })
  }

  async function handlePropose(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!selected) return
    setProposalError('')
    setProposalSuccess('')

    const fd = new FormData(e.currentTarget)
    const rawPrice = parseFloat(fd.get('price') as string)
    if (!rawPrice || rawPrice <= 0) {
      setProposalError('Le tarif est obligatoire et doit être supérieur à 0 €')
      const priceInput = e.currentTarget.querySelector<HTMLElement>('[name="price"]')
      priceInput?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setTimeout(() => priceInput?.focus({ preventScroll: true }), 250)
      return
    }

    setProposing(true)
    const dto = {
      price: Math.round(rawPrice * 100),
      message: (fd.get('message') as string) || undefined,
    }
    try {
      const proposal = await apiFetch<{ id: string }>(`/training-requests/cf/${selected.id}/proposals`, {
        method: 'POST', body: JSON.stringify(dto),
      })
      setProposalSuccess('Proposition envoyée ✓')
      ;(e.target as HTMLFormElement).reset()
      setAvailable((prev) => prev.map((r) =>
        r.id === selected.id
          ? { ...r, proposals: [{ id: proposal.id, status: 'PENDING' }], _count: { proposals: r._count.proposals + (r.proposals.length === 0 ? 1 : 0) } }
          : r,
      ))
      setSelected((s) => s ? { ...s, proposals: [{ id: proposal.id, status: 'PENDING' }] } : s)
      apiFetch<MyProposal[]>('/training-requests/cf/my-proposals').then(setMyProposals).catch(() => {})
    } catch (err: unknown) {
      setProposalError(err instanceof Error ? err.message : 'Erreur lors de l\'envoi')
    } finally {
      setProposing(false)
    }
  }

  async function handleSearch(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSearchError('')
    if (!selected) return
    const requestId = selected.id

    const searchErrs: string[] = []
    if (!searchForm.title.trim()) searchErrs.push('Intitulé de la mission')
    if (!searchForm.description.trim()) searchErrs.push('Description')
    if (!searchForm.address.trim()) searchErrs.push('Adresse')
    if (!searchForm.postalCode.trim()) searchErrs.push('Code postal')
    if (!searchForm.city.trim()) searchErrs.push('Ville')
    if (!searchForm.date) searchErrs.push('Date')
    if (!searchForm.amount || searchForm.amount <= 0) searchErrs.push('Budget')
    if (searchErrs.length > 0) {
      setSearchError(`Champs obligatoires manquants : ${searchErrs.join(', ')}`)
      scrollToFirstError(e.currentTarget)
      return
    }

    setSearchSubmitting(true)
    try {
      const mission = await apiFetch<{ id: string }>('/missions', {
        method: 'POST',
        body: JSON.stringify({
          title: searchForm.title,
          description: searchForm.description,
          type: 'TRAINING',
          address: searchForm.address,
          city: searchForm.city,
          postalCode: searchForm.postalCode,
          date: new Date(searchForm.date).toISOString(),
          duration: searchForm.duration,
          amount: searchForm.amount,
          radiusKm: searchForm.radiusKm,
          minParticipants: searchForm.minParticipants || undefined,
          requiredSpecialties: Object.keys(searchSpecialties).length > 0
            ? Object.entries(searchSpecialties).map(([specialty, quantity]) => ({ specialty, quantity }))
            : undefined,
        }),
      })
      setSearchResults((prev) => {
        const next = { ...prev, [requestId]: mission }
        try { localStorage.setItem('cf_search_results', JSON.stringify(next)) } catch {}
        return next
      })
      setSearchMode(false)
    } catch (err: unknown) {
      setSearchError(err instanceof Error ? err.message : 'Erreur lors de la création')
    } finally {
      setSearchSubmitting(false)
    }
  }

  const trainingSpecialties = specialtyConfigs.filter((s) => s.active && (s.category === 'TRAINING' || s.category === 'BOTH'))

  if (loading) return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => <div key={i} className="h-24 glass-card rounded-xl animate-pulse" />)}
    </div>
  )

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary">Demandes de formation</h1>
        <p className="text-text-secondary text-sm mt-1">Répondez aux entreprises et lancez votre propre recherche de formateur si besoin.</p>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/[0.08] mb-6 w-fit">
        {([
          { key: 'available', label: `Disponibles (${available.filter(r => r.status === 'OPEN').length})` },
          { key: 'my-proposals', label: `Mes propositions (${myProposals.length})` },
        ] as { key: Tab; label: string }[]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setTab(key); setSelected(null) }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === key ? 'bg-white/[0.10] text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Demandes disponibles ── */}
      {tab === 'available' && (
        <div className="space-y-3">
          {available.length === 0 && (
            <div className="glass-card p-10 text-center">
              <p className="text-4xl mb-3">📭</p>
              <p className="text-text-primary font-medium mb-1">Aucune demande disponible</p>
              <p className="text-text-secondary text-sm">Les nouvelles demandes d'entreprises apparaîtront ici.</p>
            </div>
          )}

          {available.map((r) => {
            const myProposal = r.proposals[0]
            const isOpen = selected?.id === r.id
            const searchResult = searchResults[r.id] ?? null
            return (
              <div key={r.id} className="glass-card rounded-xl overflow-hidden">
                {/* En-tête cliquable */}
                <button onClick={() => openDetail(r)} className="w-full text-left p-5 hover:bg-white/[0.03] transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-text-primary font-medium">{r.topic}</p>
                        {r.requiresQualiopi && (
                          <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/25 text-[#F59E0B] text-[10px] font-semibold">Qualiopi requis</span>
                        )}
                        {r.isRemote && (
                          <span className="px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.10] text-text-muted text-[10px]">À distance</span>
                        )}
                      </div>
                      <p className="text-text-muted text-xs">
                        {r.company.name} · {r.targetCount} participant{r.targetCount > 1 ? 's' : ''}
                        {r.budget ? ` · Budget ${formatEur(r.budget)}` : ''}
                        {r.location ? ` · ${r.location}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {searchResult && (
                        <Link
                          to={`/missions/${searchResult.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-success/10 border-success/30 text-success hover:bg-success/20 transition-all"
                        >
                          🔔 Mission publiée
                        </Link>
                      )}
                      {myProposal ? (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${PROPOSAL_STATUS_COLOR[myProposal.status]}`}>
                          {PROPOSAL_STATUS_LABEL[myProposal.status]}
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-white/[0.06] border-white/[0.10] text-text-muted">
                          Pas encore proposé
                        </span>
                      )}
                      <span className="text-text-muted text-xs">{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>
                </button>

                {/* Détail dépliant */}
                {isOpen && (
                  <div className="border-t border-white/[0.08]">

                    {/* Description + infos */}
                    <div className="px-5 pt-4 pb-3">
                      <p className="text-text-secondary text-sm leading-relaxed">{r.description}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-xs text-text-muted">
                        {r.preferredDate && <span>📅 {new Date(r.preferredDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
                        {r.location && <span>📍 {r.location}</span>}
                        {r.budget && <span>💰 Budget {formatEur(r.budget)}</span>}
                        <span>👥 {r.targetCount} participant{r.targetCount > 1 ? 's' : ''}</span>
                      </div>
                    </div>

                    {/* Bloc proposition */}
                    <div className="px-5 pb-4 border-b border-white/[0.06]">
                      {myProposal?.status === 'ACCEPTED' && (
                        <div className="px-4 py-3 rounded-xl bg-success/[0.06] border border-success/25 text-success text-sm font-medium">
                          Votre proposition a été acceptée par l'entreprise.
                        </div>
                      )}
                      {myProposal?.status === 'PENDING' && (
                        <div className="px-4 py-3 rounded-xl bg-accent/[0.06] border border-accent/20 text-accent text-sm">
                          Proposition envoyée — en attente de réponse de l'entreprise.
                        </div>
                      )}
                      {(!myProposal || myProposal.status === 'DECLINED') && (
                        <form onSubmit={handlePropose} className="space-y-3">
                          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                            {myProposal?.status === 'DECLINED' ? 'Renvoyer une proposition' : 'Proposer vos services à l\'entreprise'}
                          </h3>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="glass-label">Votre tarif (€) <span className="text-danger">*</span></label>
                              <input name="price" type="number" min={1} step="0.01" required className="glass-input" placeholder="ex. 1200" />
                            </div>
                          </div>
                          <div>
                            <label className="glass-label">Message (optionnel)</label>
                            <textarea name="message" rows={2} className="glass-input resize-none" placeholder="Présentez votre approche, vos certifications, votre expérience…" />
                          </div>
                          {proposalError && <p className="text-danger text-sm">{proposalError}</p>}
                          {proposalSuccess && <p className="text-success text-sm">{proposalSuccess}</p>}
                          <button type="submit" disabled={proposing} className="glass-btn-accent px-5 py-2 text-sm rounded-xl">
                            {proposing ? 'Envoi…' : 'Envoyer la proposition'}
                          </button>
                        </form>
                      )}
                    </div>

                    {/* ── Bloc recherche de prestataire ── */}
                    <div className="px-5 py-4">
                      {searchResult ? (
                        <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-success/[0.06] border border-success/25">
                          <div>
                            <p className="text-success text-sm font-medium">Mission publiée — prestataires notifiés</p>
                            <p className="text-text-muted text-xs mt-0.5">Les formateurs à proximité ont reçu une notification push.</p>
                          </div>
                          <Link
                            to={`/missions/${searchResult.id}`}
                            className="px-3 py-1.5 rounded-xl text-xs bg-success/15 border border-success/30 text-success hover:bg-success/25 transition-all flex-shrink-0"
                          >
                            Voir la mission →
                          </Link>
                        </div>
                      ) : !searchMode ? (
                        <button
                          onClick={() => setSearchMode(true)}
                          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/[0.15] text-text-secondary text-sm hover:border-accent/40 hover:text-accent hover:bg-accent/[0.04] transition-all w-full justify-center"
                        >
                          <span>🔍</span>
                          Lancer une recherche de formateur sur Velink
                        </button>
                      ) : (
                        <form onSubmit={handleSearch} className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Rechercher un formateur</h3>
                            <button type="button" onClick={() => setSearchMode(false)} className="text-text-muted text-xs hover:text-text-secondary transition-colors">Annuler</button>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                              <label className="glass-label">Intitulé de la mission <span className="text-danger">*</span></label>
                              <input
                                value={searchForm.title}
                                onChange={(e) => setSearchForm((f) => ({ ...f, title: e.target.value }))}
                                required className="glass-input" placeholder="Ex: Formation SST — 12 personnes"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="glass-label">Description <span className="text-danger">*</span></label>
                              <textarea
                                value={searchForm.description}
                                onChange={(e) => setSearchForm((f) => ({ ...f, description: e.target.value }))}
                                rows={2} required className="glass-input resize-none"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                              <label className="glass-label">Adresse <span className="text-danger">*</span></label>
                              <input
                                value={searchForm.address}
                                onChange={(e) => setSearchForm((f) => ({ ...f, address: e.target.value }))}
                                required className="glass-input" placeholder="12 rue de la Paix"
                              />
                            </div>
                            <div>
                              <label className="glass-label">Code postal <span className="text-danger">*</span></label>
                              <input
                                value={searchForm.postalCode}
                                onChange={(e) => setSearchForm((f) => ({ ...f, postalCode: e.target.value }))}
                                required pattern="\d{5}" className="glass-input" placeholder="01000"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="glass-label">Ville <span className="text-danger">*</span></label>
                              <input
                                value={searchForm.city}
                                onChange={(e) => setSearchForm((f) => ({ ...f, city: e.target.value }))}
                                required className="glass-input" placeholder="Lyon"
                              />
                            </div>
                            <div>
                              <label className="glass-label">Rayon (km)</label>
                              <input
                                type="number" value={searchForm.radiusKm} min={1} max={200}
                                onChange={(e) => setSearchForm((f) => ({ ...f, radiusKm: Number(e.target.value) }))}
                                className="glass-input"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="glass-label">Date <span className="text-danger">*</span></label>
                              <input
                                type="datetime-local" value={searchForm.date} required
                                onChange={(e) => setSearchForm((f) => ({ ...f, date: e.target.value }))}
                                className="glass-input"
                              />
                            </div>
                            <div>
                              <label className="glass-label">Durée <span className="text-danger">*</span></label>
                              <select
                                value={searchForm.duration}
                                onChange={(e) => setSearchForm((f) => ({ ...f, duration: Number(e.target.value) }))}
                                className="glass-input"
                              >
                                {DURATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="glass-label">Budget (€) <span className="text-danger">*</span></label>
                              <input
                                type="number" value={searchForm.amount || ''} min={1} required
                                onChange={(e) => setSearchForm((f) => ({ ...f, amount: Number(e.target.value) }))}
                                className="glass-input" placeholder="ex. 800"
                              />
                            </div>
                            <div>
                              <label className="glass-label">Participants min.</label>
                              <input
                                type="number" value={searchForm.minParticipants || ''} min={1}
                                onChange={(e) => setSearchForm((f) => ({ ...f, minParticipants: Number(e.target.value) }))}
                                className="glass-input" placeholder="ex. 10"
                              />
                            </div>
                          </div>

                          {/* Spécialités requises */}
                          {trainingSpecialties.length > 0 && (
                            <div>
                              <label className="glass-label">Spécialités requises</label>
                              <div className="grid grid-cols-2 gap-2 mt-1">
                                {trainingSpecialties.map((s) => {
                                  const qty = searchSpecialties[s.specialty] ?? 0
                                  const checked = qty > 0
                                  return (
                                    <div
                                      key={s.specialty}
                                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${checked ? 'bg-accent/10 border-accent/30' : 'border-white/[0.08] hover:border-white/[0.15]'}`}
                                    >
                                      <input
                                        type="checkbox" checked={checked}
                                        onChange={() => setSearchSpecialties((prev) => {
                                          if (prev[s.specialty]) { const n = { ...prev }; delete n[s.specialty]; return n }
                                          return { ...prev, [s.specialty]: 1 }
                                        })}
                                        className="accent-[#6E7FFF] w-4 h-4 flex-shrink-0 cursor-pointer"
                                      />
                                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSearchSpecialties((prev) => {
                                        if (prev[s.specialty]) { const n = { ...prev }; delete n[s.specialty]; return n }
                                        return { ...prev, [s.specialty]: 1 }
                                      })}>
                                        <div className="flex items-center gap-1.5">
                                          <span>{s.icon || '🎓'}</span>
                                          <span className="text-xs font-medium text-text-primary">{s.shortLabel || s.label}</span>
                                        </div>
                                      </div>
                                      {checked && (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                          <button type="button" onClick={() => setSearchSpecialties((p) => ({ ...p, [s.specialty]: Math.max(1, (p[s.specialty] ?? 1) - 1) }))}
                                            className="w-5 h-5 rounded bg-white/[0.08] hover:bg-white/[0.15] text-xs flex items-center justify-center">−</button>
                                          <span className="w-4 text-center text-xs font-semibold">{qty}</span>
                                          <button type="button" onClick={() => setSearchSpecialties((p) => ({ ...p, [s.specialty]: (p[s.specialty] ?? 1) + 1 }))}
                                            className="w-5 h-5 rounded bg-white/[0.08] hover:bg-white/[0.15] text-xs flex items-center justify-center">+</button>
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {searchError && <p className="text-danger text-sm">{searchError}</p>}
                          <button type="submit" disabled={searchSubmitting} className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl">
                            {searchSubmitting ? 'Publication…' : '🔔 Publier la mission et notifier les formateurs'}
                          </button>
                        </form>
                      )}
                    </div>

                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Mes propositions ── */}
      {tab === 'my-proposals' && (
        <div className="space-y-3">
          {myProposals.length === 0 && (
            <div className="glass-card p-10 text-center">
              <p className="text-4xl mb-3">📝</p>
              <p className="text-text-primary font-medium mb-1">Aucune proposition envoyée</p>
              <p className="text-text-secondary text-sm">Vos propositions aux entreprises apparaîtront ici.</p>
            </div>
          )}
          {myProposals.map((p) => (
            <div key={p.id} className="glass-card p-5 rounded-xl">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary font-medium">{p.request.topic}</p>
                  <p className="text-text-muted text-xs mt-0.5">{p.request.company.name} · {new Date(p.createdAt).toLocaleDateString('fr-FR')}</p>
                  {p.message && <p className="text-text-secondary text-sm mt-2 leading-relaxed">{p.message}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <p className="text-text-primary font-semibold">{formatEur(p.price)}</p>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${PROPOSAL_STATUS_COLOR[p.status]}`}>
                    {PROPOSAL_STATUS_LABEL[p.status]}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
